import { useState } from 'react'
import { useLocation, useSearch } from 'wouter'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ds'
import { api, ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'
import { LARGO_MINIMO, contrasenaAceptable } from '@/lib/contrasena'
import {
  AccesoCuerpo,
  AccesoLayout,
  AccesoPieTarjeta,
  AccesoTitulo,
  AvisoAcceso,
  BotonVerContrasena,
  CampoAcceso,
  EtiquetaFuerza,
  FuerzaContrasena,
  PasosAcceso,
  claseBotonAcceso,
} from '@/components/auth'

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.facilcontrol.app'

/**
 * Cuarto paso del riel de recuperación. Los tres primeros los muestra
 * `RecuperarContrasena`; acá se llega por el enlace del correo, que puede ser
 * varias horas —o días— después, así que el riel se repite para reconectar a
 * la persona con el trámite que había empezado.
 *
 * Va en modo compacto: cuatro etiquetas no caben en la tarjeta de 440px y se
 * montaban unas sobre otras.
 *
 * Y sólo se muestra cuando esto ES una recuperación. Con `flow=web|mobile` la
 * persona nunca tipeó su RUT ni eligió un correo —la empresa la dio de alta y
 * le llegó el enlace—, así que un riel con esos tres pasos marcados en verde
 * le cuenta una historia que no vivió.
 */
const PASOS = ['Tu RUT', 'Tu correo', 'Enlace enviado', 'Clave nueva']

interface ResetPasswordResponse {
  message: string
  acceso_web: boolean
  acceso_movil: boolean
}

export function ResetPassword() {
  const [, navigate] = useLocation()
  const search = useSearch()
  const params = new URLSearchParams(search)
  const token = params.get('token') ?? ''
  const rut = params.get('rut') ?? ''
  const email = params.get('email') ?? ''
  const flowHint = params.get('flow') ?? ''

  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')
  const [password2Tocada, setPassword2Tocada] = useState(false)
  const [verPassword, setVerPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<{ titulo: string; detalle?: string } | null>(null)
  const [success, setSuccess] = useState<ResetPasswordResponse | null>(null)

  // `flow` viene del correo y dice para qué se está definiendo la clave. Sirve
  // para no prometerle un panel web a alguien que solo tiene la app móvil.
  const esAlta = flowHint === 'mobile' || flowHint === 'web'

  const passwordOk = contrasenaAceptable(password)
  const password2Desajustada = password2.length > 0 && password2 !== password
  const password2Ok = password2.length > 0 && !password2Desajustada
  const enlaceValido = Boolean(token && rut)
  const puedeEnviar = passwordOk && password2Ok && enlaceValido

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isLoading || !puedeEnviar) {
      setPassword2Tocada(true)
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const res = await api.post<ResetPasswordResponse>('/auth/reset-password', {
        token,
        rut,
        password,
        password_confirmation: password2,
      })
      setSuccess(res)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 422) {
          setError({
            titulo: 'El enlace expiró o ya se usó',
            detalle: 'Pide uno nuevo desde “¿La olvidaste?” en el inicio de sesión.',
          })
        } else {
          setError({ titulo: err.message || 'No pudimos guardar la contraseña' })
        }
      } else {
        setError({
          titulo: 'No se pudo conectar con el servidor',
          detalle: 'Revisa tu conexión a internet y vuelve a intentar.',
        })
      }
    } finally {
      setIsLoading(false)
    }
  }

  // --- Contraseña guardada -------------------------------------------------
  if (success) {
    const { acceso_web: web, acceso_movil: movil } = success
    return (
      <AccesoLayout>
        <div className="flex flex-col items-center gap-4 px-[26px] pt-6 pb-[22px] text-center">
          <div className="bg-success-bg flex size-12 items-center justify-center rounded-full">
            <Icon name="check_circle" size={26} className="text-success" />
          </div>
          <div>
            <h1 className="mb-1.5 text-[19px] font-bold tracking-[-0.02em]">Contraseña guardada</h1>
            <p className="text-body text-[13px] leading-[1.55]">
              Ya puedes entrar a Fácil Control con tu RUT y la contraseña que acabas de definir.
            </p>
          </div>

          <div className="flex w-full flex-col gap-2.5 text-left">
            {movil && (
              <div className="border-border flex flex-col gap-2.5 rounded-[10px] border p-[13px]">
                <div className="flex items-start gap-[9px]">
                  <Icon name="smartphone" size={18} className="text-primary mt-px" />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] font-semibold">Acceso móvil</span>
                    <span className="text-body text-[11.5px] leading-[1.5]">
                      Abre la app de Fácil Control desde tu celular e ingresa con tu RUT.
                    </span>
                  </div>
                </div>
                <Button asChild variant="outline" className="h-[38px] w-full">
                  <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">
                    Descargar la app desde Play Store
                  </a>
                </Button>
              </div>
            )}

            {web && (
              <div className="border-border flex flex-col gap-2.5 rounded-[10px] border p-[13px]">
                <div className="flex items-start gap-[9px]">
                  <Icon name="desktop_windows" size={18} className="text-primary mt-px" />
                  <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-[13px] font-semibold">Acceso al panel web</span>
                    <span className="text-body text-[11.5px] leading-[1.5]">
                      Ingresa al panel con tu RUT y la contraseña que acabas de configurar.
                    </span>
                  </div>
                </div>
                <Button onClick={() => navigate('/login')} className="h-[38px] w-full">
                  Ir al panel web
                  <Icon name="arrow_forward" size={17} />
                </Button>
              </div>
            )}

            {!web && !movil && (
              <AvisoAcceso
                titulo="Tu cuenta todavía no tiene acceso configurado"
                detalle="Habla con tu administrador para que te asigne una licencia móvil o acceso al panel."
              />
            )}
          </div>
        </div>
      </AccesoLayout>
    )
  }

  // --- Definir contraseña --------------------------------------------------
  return (
    <AccesoLayout>
      {!esAlta && <PasosAcceso pasos={PASOS} actual={4} compacto />}

      <form onSubmit={handleSubmit} noValidate>
        {/* Con riel arriba alcanza con 20px; sin riel vuelve al padding
            normal de la tarjeta, o el título queda pegado al borde. */}
        <AccesoCuerpo className={esAlta ? undefined : 'pt-5'}>
          <AccesoTitulo
            titulo={esAlta ? 'Define tu contraseña' : 'Crea una contraseña nueva'}
            detalle={
              email
                ? `Va a ser la contraseña de la cuenta asociada a ${email}. Para entrar sigues usando tu RUT.`
                : 'Con esta contraseña y tu RUT vas a entrar a Fácil Control.'
            }
          />

          {!enlaceValido && (
            <AvisoAcceso
              titulo="Este enlace está incompleto"
              detalle="Ábrelo directo desde el correo que te enviamos, o pide uno nuevo desde el inicio de sesión."
            />
          )}

          {error && <AvisoAcceso titulo={error.titulo} detalle={error.detalle} />}

          <div className="flex flex-col gap-1.5">
            <CampoAcceso
              id="reset-password"
              label="Contraseña"
              icono="lock"
              type={verPassword ? 'text' : 'password'}
              placeholder={`Mínimo ${LARGO_MINIMO} caracteres`}
              autoComplete="new-password"
              value={password}
              onChange={(v) => {
                setPassword(v)
                setError(null)
              }}
              disabled={isLoading || !enlaceValido}
              accion={<EtiquetaFuerza clave={password} />}
              sufijo={
                <BotonVerContrasena
                  visible={verPassword}
                  onToggle={() => setVerPassword((v) => !v)}
                />
              }
            />
            <FuerzaContrasena clave={password} />
          </div>

          <CampoAcceso
            id="reset-password2"
            label="Repite la contraseña"
            icono="lock"
            type={verPassword ? 'text' : 'password'}
            placeholder="La misma de arriba"
            autoComplete="new-password"
            value={password2}
            onChange={(v) => {
              setPassword2(v)
              setError(null)
            }}
            onBlur={() => setPassword2Tocada(true)}
            listo={password2Ok}
            error={
              password2Desajustada && password2Tocada ? 'Las contraseñas no coinciden' : ''
            }
            disabled={isLoading || !enlaceValido}
          />

          <Button
            type="submit"
            size="lg"
            className={cn('w-full', claseBotonAcceso(puedeEnviar))}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Icon name="progress_activity" size={18} className="animate-spin" />
                Guardando…
              </>
            ) : (
              'Guardar contraseña'
            )}
          </Button>
        </AccesoCuerpo>
      </form>

      <AccesoPieTarjeta className="justify-center gap-4 py-[13px]">
        <button
          type="button"
          onClick={() => navigate('/login')}
          className="text-primary-hover hover:text-primary-strong cursor-pointer text-[12.5px] font-medium"
        >
          Volver al inicio de sesión
        </button>
      </AccesoPieTarjeta>
    </AccesoLayout>
  )
}
